import React, { useState, useRef, useEffect } from 'react';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { toast } from 'react-toastify';

import { Card } from '../../card';
import Button from '../../Button';
import { getMicrophoneStream, getRecorderOptions, createRunningAudioContext, waitForLiveAudioTrack } from '../../../utils/speechUtils';

const AudioRecorder = ({ disabled, onComplete, onStart, sampleRate, channelCount }) => {
    const [recording, setRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [audioURL, setAudioURL] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioUrls, setAudioUrls] = useState({});

    // One live recording: { recorder, stream, chunks, aborted }. Each recording
    // owns its chunks — a buffer shared between recorders loses the container
    // header of whichever started first, and the upload then fails to decode.
    const activeSessionRef = useRef(null);
    // Covers the window between the click and getUserMedia resolving, which the
    // `recording` state cannot (setRecording(true) runs only after the await).
    const startingRef = useRef(false);
    const streamRef = useRef(null);
    const timerRef = useRef(null);
    const canvasRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const dataArrayRef = useRef(null);
    const audioPlayerRef = useRef(null);
    // Retain the source node so Chromium can't garbage-collect it mid-recording
    // (which would silence the analyser feeding the waveform).
    const sourceRef = useRef(null);

    const drawWaveform = () => {
        if (!analyserRef.current || !dataArrayRef.current || !canvasRef.current) {
            return;
        }

        const canvas = canvasRef.current;
        const canvasCtx = canvas.getContext('2d');
        const analyser = analyserRef.current;
        const dataArray = dataArrayRef.current;

        const draw = () => {

            analyser.getByteTimeDomainData(dataArray);

            canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
            canvasCtx.fillStyle = '#fff';
            canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

            canvasCtx.lineWidth = 2;
            canvasCtx.strokeStyle = 'rgb(255, 0, 0)';

            canvasCtx.beginPath();
            const sliceWidth = canvas.width / dataArray.length;
            let x = 0;

            for (let i = 0; i < dataArray.length; i++) {
                const v = dataArray[i] / 128.0;
                const y = (v * canvas.height) / 2;

                if (i === 0) {
                    canvasCtx.moveTo(x, y);
                } else {
                    canvasCtx.lineTo(x, y);
                }

                x += sliceWidth;
            }

            canvasCtx.lineTo(canvas.width, canvas.height / 2);
            canvasCtx.stroke();

            requestAnimationFrame(draw);
        };

        draw();
    };

    const startRecording = () => {
        if (disabled) return;
        if (recording) return stopRecording();
        // Second click before getUserMedia resolves — without this, two recorders
        // end up sharing one microphone and one chunk buffer.
        if (startingRef.current) return;
        startingRef.current = true;

        if (onStart && typeof onStart === 'function') onStart();

        getMicrophoneStream()
            .then(async stream => {
                // Retire any recorder this one supersedes; left running it holds
                // the mic open and keeps emitting chunks.
                const previous = activeSessionRef.current;
                if (previous) {
                    previous.aborted = true;
                    try {
                        if (previous.recorder.state !== 'inactive') previous.recorder.stop();
                    } catch (e) { /* already stopped */ }
                    previous.stream.getTracks().forEach(track => track.stop());
                }

                streamRef.current = stream;
                await waitForLiveAudioTrack(stream); // post-idle tracks can start muted
                // Resumed context: a suspended one (idle/backgrounded tab) feeds the
                // analyser silence and wrongly trips the "no audio" detector.
                const audioContext = await createRunningAudioContext({ sampleRate });
                const source = audioContext.createMediaStreamSource(stream);
                sourceRef.current = source;
                const merger = audioContext.createChannelMerger(channelCount);
                const destination = audioContext.createMediaStreamDestination();
                source.connect(merger);
                merger.connect(destination);
                const recorder = new MediaRecorder(destination.stream, getRecorderOptions());
                const analyser = audioContext.createAnalyser();
                source.connect(analyser);

                analyser.fftSize = 2048;
                const bufferLength = analyser.frequencyBinCount;
                const dataArray = new Uint8Array(bufferLength);

                audioContextRef.current = audioContext;
                analyserRef.current = analyser;
                dataArrayRef.current = dataArray;

                drawWaveform();

                // Chunks belong to THIS recording; only the first carries the
                // container header.
                const chunks = [];
                const session = { recorder, stream, chunks, aborted: false };
                activeSessionRef.current = session;

                recorder.ondataavailable = event => {
                    if (event.data.size > 0) {
                        chunks.push(event.data);
                    }
                };
                recorder.onstop = () => {
                    stream.getTracks().forEach(track => track.stop()); // Stop all tracks

                    // Superseded or aborted: release the hardware, publish nothing.
                    // This capture is partial and would only fail server-side.
                    if (session.aborted || activeSessionRef.current !== session) return;
                    activeSessionRef.current = null;

                    // Type comes from the recorder, not a guess: this blob is
                    // uploaded, and the server whitelists by extension.
                    const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
                    setAudioBlob(blob);
                    setAudioURL(URL.createObjectURL(blob)); // Set the audio URL for playback
                    audioPlayerRef.current?.load(); // Reload the audio element for playback
                };
                recorder.start(100); // Collect 100ms chunks of audio
                setRecording(true);
                setElapsedTime(0); // Reset timer
                setAudioBlob(null)
            })
            .catch(error => toast.error('Error accessing microphone: ' + error.message))
            .finally(() => { startingRef.current = false; });
    };

    const stopRecording = () => {
        const session = activeSessionRef.current;
        if (!session) return;
        try {
            if (session.recorder.state !== 'inactive') session.recorder.stop();
        } catch (e) { /* already stopped */ }
        setRecording(false);
    };

    const handlePlayPause = () => {
        if (isPlaying) {
            audioPlayerRef.current.pause();
        } else {
            audioPlayerRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const formatTime = time => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    useEffect(() => {
        if (recording) {
            timerRef.current = setInterval(() => {
                setElapsedTime(prevTime => prevTime + 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }

        return () => clearInterval(timerRef.current);
    }, [recording]);


    useEffect(() => {
        const audioElement = audioPlayerRef.current;
        if (audioElement) {
            const handleEnded = () => {
                setIsPlaying(false);

                if (Object.keys(audioUrls)) {
                    let _word = audioPlayerRef.current.getAttribute('text');
                    setAudioUrls(prev => ({ ...prev, [_word]: { ...prev[_word], playing: false } }))
                }
            };
            audioElement.addEventListener('ended', handleEnded);
            return () => {
                audioElement.removeEventListener('ended', handleEnded);
            };
        }
    }, [audioPlayerRef]);

    useEffect(() => {
        if (!recording && !audioBlob) return;
        if (onComplete && typeof onComplete === "function") {
            onComplete(audioBlob);
        }
    }, [audioBlob]);

    return (
        <div style={styles.recorderContainer}>
            <div style={{ width: '50%' }}>
                <Card style={styles.container}>
                    <div style={styles.timer}>
                        {formatTime(elapsedTime)}
                    </div>

                    <canvas ref={canvasRef} style={{ display: recording ? 'block' : 'none', width: '100%', height: '100px' }}></canvas>
                    <div style={styles.actionBtnContainer}>
                        <Button style={styles.recButton} onClick={startRecording} variant='contained' disabled={disabled || isPlaying}
                            placeholder={recording ? <StopCircleIcon fontSize='large' /> : <MicIcon fontSize='large' />}
                        />
                    </div>
                    {recording && <div style={styles.statusMessage}>Recording...</div>}
                    <div style={{ display: audioBlob ? 'flex' : 'none', justifyContent: 'center', marginTop: '1rem' }}>
                        <audio src={audioURL} style={{ width: '18px' }} ref={audioPlayerRef} />
                        <Button variant='outlined' style={{ height: '40px', width: '40px', borderRadius: '40px' }} onClick={handlePlayPause} placeholder={isPlaying ? <PauseIcon /> : <PlayArrowIcon />} />
                    </div>

                </Card>
            </div>
        </div>

    );
};

const styles = {
    container: {
        marginTop: '5px',
        marginBottom: '5px',
        padding: '5px',
    },
    timer: {
        marginBottom: '20px',
        marginTop: '1rem',
        fontWeight: '500',
        fontSize: '2.1rem',
        textAlign: 'center'
    },
    actionBtnContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '1rem'
    },
    recButton: {
        width: '90px',
        height: '58px',
        borderRadius: '9999px',
    },
    recorderContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '20px',
        marginBottom: '20px',
    },
    statusMessage: {
        marginTop: '1rem',
        textAlign: 'center'
    },
}

export default AudioRecorder;
